import { FC } from "react";
import { motion } from "framer-motion";
import {
  PlusCircle,
  Filter,
  Clock,
  FileText,
  XCircle,
  Loader,
  Building2,
  AlertCircle,
  TrendingUp,
  CheckCircle,
} from "lucide-react";

export interface ModernPendingSpacesHeaderProps {
  totalCount: number;
  pendingCount: number;
  draftCount: number;
  blockedCount: number;
  processingCount: number;
  onAddSpace: () => void;
  onFilter: (status?: string) => void;
}

const ModernPendingSpacesHeader: FC<ModernPendingSpacesHeaderProps> = ({
  totalCount,
  pendingCount,
  draftCount,
  blockedCount,
  processingCount,
  onAddSpace,
  onFilter,
}) => {
  const stats = [
    {
      label: "En attente",
      count: pendingCount,
      icon: <Clock className="w-5 h-5" />,
      color: "from-orange-500 to-amber-600",
      bgColor: "bg-orange-100 dark:bg-orange-900/20",
      textColor: "text-orange-700 dark:text-orange-300",
      description: "En cours d'examen",
    },
    {
      label: "Brouillons",
      count: draftCount,
      icon: <FileText className="w-5 h-5" />,
      color: "from-gray-500 to-slate-600",
      bgColor: "bg-gray-100 dark:bg-gray-900/20",
      textColor: "text-gray-700 dark:text-gray-300",
      description: "À compléter",
    },
    {
      label: "En cours",
      count: processingCount,
      icon: <Loader className="w-5 h-5" />,
      color: "from-blue-500 to-indigo-600",
      bgColor: "bg-blue-100 dark:bg-blue-900/20",
      textColor: "text-blue-700 dark:text-blue-300",
      description: "En traitement",
    },
    {
      label: "Bloqués",
      count: blockedCount,
      icon: <XCircle className="w-5 h-5" />,
      color: "from-red-500 to-rose-600",
      bgColor: "bg-red-100 dark:bg-red-900/20",
      textColor: "text-red-700 dark:text-red-300",
      description: "À corriger",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="mb-8"
    >
      {/* Main Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
        <div className="mb-6 lg:mb-0">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center space-x-3 mb-3"
          >
            <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
              <AlertCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent dark:from-white dark:to-gray-300">
                Mes Demandes d'Espaces
              </h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {totalCount} demande{totalCount !== 1 ? "s" : ""} en cours de traitement
              </p>
            </div>
          </motion.div>

          {/* Status Summary */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex items-center space-x-4 text-sm"
          >
            {pendingCount > 0 && (
              <div className="flex items-center space-x-1 px-3 py-1 bg-orange-50 dark:bg-orange-900/20 rounded-full">
                <Clock className="w-4 h-4 text-orange-600" />
                <span className="text-orange-700 dark:text-orange-300 font-medium">{pendingCount} en attente</span>
              </div>
            )}
            {draftCount > 0 && (
              <div className="flex items-center space-x-1 px-3 py-1 bg-gray-50 dark:bg-gray-900/20 rounded-full">
                <FileText className="w-4 h-4 text-gray-600" />
                <span className="text-gray-700 dark:text-gray-300 font-medium">
                  {draftCount} brouillon{draftCount > 1 ? "s" : ""}
                </span>
              </div>
            )}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-3"
        >
          <button
            onClick={() => onFilter()}
            className="flex items-center space-x-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <Filter className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            <span className="text-gray-700 dark:text-gray-300 font-medium">Filtres</span>
          </button>

          <button
            onClick={onAddSpace}
            className="flex items-center space-x-2 px-6 py-2.5 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-xl hover:from-orange-700 hover:to-amber-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <PlusCircle className="w-4 h-4" />
            <span className="font-semibold">Nouvel espace</span>
          </button>
        </motion.div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * index }}
            whileHover={{ scale: 1.02 }}
            className={`${stat.bgColor} rounded-xl p-4 cursor-pointer transition-all duration-200 hover:shadow-lg border border-gray-100 dark:border-gray-800`}
            onClick={() => onFilter(stat.label.toLowerCase())}
          >
            <div className="flex items-center justify-between mb-2">
              <div className={`p-2 rounded-lg ${stat.textColor} ${stat.bgColor}`}>{stat.icon}</div>
              <div className="text-right">
                <div className={`text-2xl font-bold ${stat.textColor}`}>{stat.count}</div>
              </div>
            </div>
            <div className={`text-sm font-medium ${stat.textColor} mb-1`}>{stat.label}</div>
            <div className={`text-xs ${stat.textColor} opacity-75`}>{stat.description}</div>
          </motion.div>
        ))}
      </div>

      {/* Help Section for Pending Requests */}
      {totalCount > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/10 dark:to-amber-900/10 rounded-xl p-4 border border-orange-200 dark:border-orange-800"
        >
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-orange-800 dark:text-orange-200 mb-1">
                Vos demandes sont en cours d'examen
              </h3>
              <p className="text-xs text-orange-700 dark:text-orange-300">
                Notre équipe examine vos espaces pour s'assurer qu'ils respectent nos critères de qualité. Vous
                recevrez une notification dès validation.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Quick Actions */}
      {totalCount > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="flex flex-wrap gap-3 mt-6"
        >
          {draftCount > 0 && (
            <button
              onClick={() => onFilter("brouillon")}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900/30 transition-colors text-sm font-medium"
            >
              <FileText className="w-4 h-4" />
              <span>Terminer les brouillons</span>
            </button>
          )}

          {pendingCount > 0 && (
            <button
              onClick={() => onFilter("en attente")}
              className="flex items-center space-x-2 px-4 py-2 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors text-sm font-medium"
            >
              <TrendingUp className="w-4 h-4" />
              <span>Suivre les demandes</span>
            </button>
          )}

          {blockedCount > 0 && (
            <button
              onClick={() => onFilter("bloqué")}
              className="flex items-center space-x-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-sm font-medium"
            >
              <CheckCircle className="w-4 h-4" />
              <span>Corriger les blocages</span>
            </button>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default ModernPendingSpacesHeader;
